/** Partner console — Overview (dashboard): KPIs + terms + quick actions. */
import { useMemo, useState } from "react";
import { formatDate } from "@/lib/format";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { Pagination } from "@/components/Pagination";
import { PartnerPageHeader } from "@/components/PartnerLayout";
import { Spinner } from "@/components/Spinner";
import {
  useDeclinePackRequest,
  useGrantSignaturePack,
  useMarkClientInvoicePaid,
  usePartnerCommercials,
  usePartnerCommissions,
  usePartnerEconomics,
  usePartnerInvoices,
  usePartnerMe,
  usePartnerPackRequests,
} from "@/lib/partner";

/* ────────────────────────────────────────────────────────────────────
   UI primitives
   ──────────────────────────────────────────────────────────────────── */

function Kpi({
  label,
  value,
  sub,
  highlight,
  trend,
}: {
  label: string;
  value: string;
  sub?: string;
  highlight?: boolean;
  /** Optional normalised (0-1) values for a tiny bar sparkline. */
  trend?: number[];
}) {
  return (
    <div
      className={`rounded-md border p-3 space-y-1 ${
        highlight
          ? "border-nb-mint bg-nb-mint/5"
          : "border-nb-border bg-slate-900/40"
      }`}
    >
      <div className="text-xs text-nb-text-3">{label}</div>
      <div className="mt-0.5 text-xl font-semibold text-nb-text">{value}</div>
      {sub && <div className="text-[11px] text-nb-text-3">{sub}</div>}
      {trend && trend.length > 1 && (
        <MiniSparkline values={trend} highlight={highlight} />
      )}
    </div>
  );
}

/** Tiny inline SVG bar chart — no external deps. */
function MiniSparkline({
  values,
  highlight,
}: {
  values: number[];
  highlight?: boolean;
}) {
  const W = 64,
    H = 20,
    gap = 2;
  const n = values.length;
  const barW = (W - gap * (n - 1)) / n;
  const color = highlight ? "#22D3EE" : "#475569";
  return (
    <svg width={W} height={H} className="mt-1 opacity-70" aria-hidden="true">
      {values.map((v, i) => {
        const bh = Math.max(2, Math.round(v * H));
        return (
          <rect
            key={i}
            x={i * (barW + gap)}
            y={H - bh}
            width={barW}
            height={bh}
            rx={1}
            fill={color}
          />
        );
      })}
    </svg>
  );
}

/* ────────────────────────────────────────────────────────────────────
   Main page
   ──────────────────────────────────────────────────────────────────── */

export default function PartnerOverviewPage() {
  const { t } = useTranslation();
  const me = usePartnerMe();
  const commissions = usePartnerCommissions();
  const [invoicePage, setInvoicePage] = useState(1);
  const invoices = usePartnerInvoices(invoicePage);
  const invoiceRows = invoices.data?.results ?? [];
  const invoiceTotal = invoices.data?.count ?? 0;
  const economics = usePartnerEconomics();
  const commercials = usePartnerCommercials();
  const markPaid = useMarkClientInvoicePaid();

  // Derived KPI values from existing data
  const {
    totalEarned,
    sessionRevTotal,
    sigRevTotal,
    commissionTrend,
    periodCount,
  } = useMemo(() => {
    const entries = commissions.data?.entries ?? [];
    let totalEarned = 0,
      sessionRevTotal = 0,
      sigRevTotal = 0;
    for (const e of entries) {
      totalEarned += Number(e.commission_eur);
      sessionRevTotal += Number(e.session_revenue_eur ?? 0);
      sigRevTotal += Number(e.signature_revenue_eur ?? 0);
    }
    // Last 8 periods as a sparkline (chronological order)
    const recent = [...entries]
      .sort(
        (a, b) =>
          new Date(a.period_start).getTime() -
          new Date(b.period_start).getTime(),
      )
      .slice(-8);
    const maxComm = Math.max(...recent.map((e) => Number(e.commission_eur)), 1);
    const commissionTrend = recent.map((e) => Number(e.commission_eur) / maxComm);
    return {
      totalEarned,
      sessionRevTotal,
      sigRevTotal,
      commissionTrend,
      periodCount: entries.length,
    };
  }, [commissions.data]);

  const commercialCount = commercials.data?.commercials.length ?? 0;

  if (me.isLoading) return <Spinner fullscreen label={t("common.loading")} />;
  if (!me.data) return null;
  const p = me.data;
  const isReseller = p.kind === "reseller";

  return (
    <>
      <PartnerPageHeader
        eyebrow={
          isReseller
            ? t("partner.kind.reseller")
            : t("partner.kind.commercial")
        }
        title={p.name}
        description={t("partner.overview.lead")}
      />

      {/* ── Main KPI grid ── */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi
          label={t("nav.partner.organizations")}
          value={String(p.organization_count)}
          sub={
            isReseller && commercialCount > 0
              ? t("partner.overview.kpiCommercialsCount", {
                  count: commercialCount,
                  defaultValue: `${commercialCount} commercials`,
                })
              : undefined
          }
        />
        <Kpi
          label={t("partner.kpi.commissionEarned")}
          value={`€${totalEarned.toFixed(2)}`}
          sub={
            periodCount > 0
              ? t("partner.overview.kpiPeriods", {
                  count: periodCount,
                  defaultValue: `over ${periodCount} period(s)`,
                })
              : undefined
          }
          trend={commissionTrend.length > 1 ? commissionTrend : undefined}
        />
        <Kpi
          label={t("partner.kpi.unpaid")}
          value={`€${commissions.data?.unpaid_total_eur ?? "0.00"}`}
          highlight
        />
        {/* Session volume */}
        <Kpi
          label={t("partner.overview.kpiSessionRev", "Session revenue billed")}
          value={`€${sessionRevTotal.toFixed(2)}`}
          sub={
            sigRevTotal > 0
              ? `+ €${sigRevTotal.toFixed(2)} ${t("partner.overview.kpiSigRev", "sig.")}`
              : undefined
          }
        />
      </div>

      {/* ── Payouts status ── */}
      {p.payouts.is_payee && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Kpi
            label={t("nav.partner.payouts")}
            value={
              p.payouts.payouts_enabled
                ? t("partner.payouts.enabledShort")
                : t("partner.payouts.notSetupShort")
            }
            highlight={!p.payouts.payouts_enabled}
          />
        </div>
      )}

      {/* ── Commission terms card ── */}
      <section className="card space-y-1">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-nb-text-2">
          {t("partner.overview.termsTitle")}
        </h2>
        <p className="text-sm text-nb-text-2">
          {p.billing_mode === "wholesale" && p.wholesale?.session_eur != null
            ? t("partner.overview.wholesaleTerms", {
                rate: Number(p.wholesale.session_eur).toFixed(2),
              })
            : `${p.terms.rate_session_pct}% / session · ${p.terms.rate_base_pct}% / base · ${p.terms.rate_signature_pct}% / signature · ${p.terms.commission_duration}`}
        </p>
        <p className="text-sm text-nb-text-3">
          {t("partner.overview.referral")}:{" "}
          <span className="font-mono text-nb-text-2">{p.referral_code}</span>
        </p>
      </section>

      {/* ── Reseller: pack-requests inbox ── */}
      {isReseller && <PackRequestsInbox />}

      {/* ── Wholesale (Model B) P&L ── */}
      {economics.data && economics.data.margin_eur !== undefined && (
        <section className="card space-y-3">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-nb-text-2">
            {t("partner.overview.pnlTitle")}
          </h2>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <Kpi
              label={t("partner.overview.pnlRevenue")}
              value={`€${economics.data.revenue_eur ?? "0"}`}
            />
            <Kpi
              label={t("partner.overview.pnlOwedNx")}
              value={`€${economics.data.owed_nexbasira_eur ?? "0"}`}
              sub={
                Number(economics.data.owed_nexbasira_pending_eur ?? 0) > 0
                  ? `+ €${economics.data.owed_nexbasira_pending_eur} ${t("partner.overview.pnlOwedPending")}`
                  : undefined
              }
            />
            <Kpi
              label={t("partner.overview.pnlOwedCom")}
              value={`€${economics.data.owed_commercials_eur ?? "0"}`}
            />
            <Kpi
              label={t("partner.overview.pnlMargin")}
              value={`€${economics.data.margin_eur ?? "0"}`}
              highlight
            />
          </div>
          {(economics.data.commercials ?? []).length > 0 && (
            <p className="text-xs text-nb-text-3">
              {t("partner.overview.pnlCommercials")}:{" "}
              {(economics.data.commercials ?? [])
                .map((c) => `${c.name} €${c.owed_eur}`)
                .join(" · ")}
            </p>
          )}
          {(economics.data.client_invoices ?? []).length > 0 && (
            <div>
              <h3 className="mb-1 text-xs uppercase text-nb-text-3">
                {t("partner.overview.pnlClientInvoices")}
              </h3>
              <table className="w-full text-left text-sm">
                <tbody>
                  {(economics.data.client_invoices ?? []).map((inv) => (
                    <tr key={inv.id} className="border-t border-nb-border">
                      <td className="py-1.5 text-nb-text-2">
                        {formatDate(inv.period_start)}
                      </td>
                      <td className="py-1.5 text-right text-nb-text">
                        €{inv.total_eur}
                      </td>
                      <td className="py-1.5 text-right">
                        {inv.status === "paid" ? (
                          <span className="rounded bg-nb-mint/15 px-1.5 py-0.5 text-[10px] text-nb-mint">
                            {t("admin.billing.invStatusPaid")}
                          </span>
                        ) : (
                          <button
                            type="button"
                            className="btn-ghost text-xs"
                            disabled={markPaid.isPending}
                            onClick={() => markPaid.mutate(inv.id)}
                          >
                            {t("partner.overview.pnlMarkPaid")}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {/* ── NexBasira invoices owed by this reseller (wholesale) ── */}
      {invoiceRows.length > 0 && (
        <section className="card space-y-2">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-nb-text-2">
            {t("partner.overview.owedTitle")}
          </h2>
          <table className="w-full text-left text-sm">
            <thead className="text-xs uppercase text-nb-text-3">
              <tr>
                <th className="py-1">{t("admin.billing.invoicePeriod")}</th>
                <th className="py-1 text-right">
                  {t("admin.billing.invoiceTotal")}
                </th>
                <th className="py-1">{t("admin.billing.invoiceStatusHdr")}</th>
                <th className="py-1" />
              </tr>
            </thead>
            <tbody>
              {invoiceRows.map((inv) => (
                <tr key={inv.id} className="border-t border-nb-border">
                  <td className="py-1.5 text-nb-text-2">
                    {formatDate(inv.period_start)}
                  </td>
                  <td className="py-1.5 text-right text-nb-text">
                    €{inv.total_eur}
                  </td>
                  <td className="py-1.5">
                    <span
                      className={`rounded px-1.5 py-0.5 text-[10px] ${
                        inv.status === "paid"
                          ? "bg-nb-mint/15 text-nb-mint"
                          : "bg-amber-500/15 text-amber-300"
                      }`}
                    >
                      {inv.status === "paid"
                        ? t("admin.billing.invStatusPaid")
                        : t("admin.billing.invStatusUnpaid")}
                    </span>
                  </td>
                  <td className="py-1.5 text-right">
                    <div className="flex justify-end gap-2">
                      {inv.status !== "paid" && inv.hosted_url && (
                        <a
                          href={inv.hosted_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn-primary text-xs"
                        >
                          {t("admin.billing.invoicePay")}
                        </a>
                      )}
                      {inv.pdf_url && (
                        <a
                          href={inv.pdf_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn-ghost text-xs"
                        >
                          PDF
                        </a>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination
            page={invoicePage}
            pageSize={30}
            count={invoiceTotal}
            onPage={setInvoicePage}
            disabled={invoices.isFetching}
          />
        </section>
      )}

      {/* ── Commercial's own commission history (non-reseller) ── */}
      {!isReseller && (commissions.data?.entries ?? []).length > 0 && (
        <CommercialHistoryCard entries={commissions.data!.entries} />
      )}

      {/* ── Quick actions ── */}
      <div className="flex flex-wrap gap-2">
        <Link to="/partner/organizations" className="btn-primary text-sm">
          {t("partner.overview.createOrgCta")}
        </Link>
        {isReseller && (
          <Link to="/partner/commercials" className="btn-ghost text-sm">
            {t("nav.partner.commercials")}
          </Link>
        )}
        <Link to="/partner/commissions" className="btn-ghost text-sm">
          {t("nav.partner.commissions")}
        </Link>
        {isReseller && (
          <Link to="/partner/settings" className="btn-ghost text-sm">
            {t("partner.settings.title", "Settings")}
          </Link>
        )}
        {p.payouts.is_payee && !p.payouts.payouts_enabled && (
          <Link to="/partner/payouts" className="btn-ghost text-sm">
            {t("partner.payouts.setupCta")}
          </Link>
        )}
      </div>
    </>
  );
}

/* ────────────────────────────────────────────────────────────────────
   Sub-components
   ──────────────────────────────────────────────────────────────────── */

/** Commercial-only: a card showing their recent per-org commission history. */
function CommercialHistoryCard({
  entries,
}: {
  entries: import("@/lib/types").PartnerCommissions["entries"];
}) {
  const { t } = useTranslation();
  const recent = [...entries].slice(0, 10);
  return (
    <section className="card space-y-2">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-nb-text-2">
        {t("partner.overview.historyTitle", "Recent commissions")}
      </h2>
      <table className="w-full text-left text-sm">
        <thead className="text-xs uppercase text-nb-text-3">
          <tr>
            <th className="py-1">{t("admin.billing.invoicePeriod")}</th>
            <th className="py-1 text-right">
              {t("partner.kpi.commissionEarned")}
            </th>
            <th className="py-1">
              {t("partner.overview.historyStatus", "Status")}
            </th>
          </tr>
        </thead>
        <tbody>
          {recent.map((e) => (
            <tr key={String(e.id)} className="border-t border-nb-border">
              <td className="py-1.5 text-nb-text-2">
                {formatDate(e.period_start)}
              </td>
              <td className="py-1.5 text-right font-semibold text-nb-text">
                €{Number(e.commission_eur).toFixed(2)}
              </td>
              <td className="py-1.5">
                <span
                  className={`rounded px-1.5 py-0.5 text-[10px] ${
                    e.status === "paid"
                      ? "bg-nb-mint/15 text-nb-mint"
                      : "bg-amber-500/15 text-amber-300"
                  }`}
                >
                  {e.status}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

/** Reseller-only: pending signature-pack requests from managed orgs, with
 * Grant (credits the org + accrues a wholesale charge) / Decline. */
function PackRequestsInbox() {
  const { t } = useTranslation();
  const requests = usePartnerPackRequests();
  const grant = useGrantSignaturePack();
  const decline = useDeclinePackRequest();
  const pending = (requests.data ?? []).filter((r) => r.status === "pending");
  if (pending.length === 0) return null;

  return (
    <section className="card space-y-2">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-nb-text-2">
        {t("partner.overview.packRequestsTitle", { count: pending.length })}
      </h2>
      <ul className="divide-y divide-nb-border text-sm">
        {pending.map((r) => (
          <li key={r.id} className="flex flex-wrap items-center gap-2 py-2">
            <div className="min-w-0 flex-1">
              <span className="text-nb-text">{r.org_name}</span>{" "}
              <span className="text-nb-text-3">
                — {r.quantity}× {r.level.toUpperCase()}
                {r.setups > 0 ? ` + ${r.setups} setups` : ""}
              </span>
              {r.requested_by_email && (
                <span className="ml-1 text-xs text-nb-text-3">
                  ({r.requested_by_email})
                </span>
              )}
            </div>
            <button
              type="button"
              className="btn-primary text-xs"
              disabled={grant.isPending}
              onClick={() =>
                grant.mutate({
                  orgId: String(r.org_id),
                  level: r.level as "l1" | "l2" | "l3",
                  quantity: r.quantity,
                  setups: r.setups,
                  request_id: r.id,
                })
              }
            >
              {t("partner.overview.packGrant")}
            </button>
            <button
              type="button"
              className="btn-ghost text-xs"
              disabled={decline.isPending}
              onClick={() => decline.mutate(r.id)}
            >
              {t("partner.overview.packDecline")}
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
